import { useState, useMemo, useEffect, useCallback } from "react";
import { can, INSTRUMENT_TYPES, STATUSES_I, CALIBRATION_STATUSES, calibrationState,
  MT_OPTIONS, MT_LABELS, MT_BADGE, classifyMaintenanceType, maintenanceTypeOf, formatTime12} from "../config";
import { apiCall } from "../api";
import { Input, Select, Btn, TimeField } from "./common/FormElements";
import InstrumentHistoryCard from "./InstrumentHistoryCard";
import { FileUploadBox } from "./common/FormElements";
import { Modal } from "./common/Modal";
import { openProtectedFile } from "./HistoryCard";
import html2pdf from "html2pdf.js";

function escapeHtml(str) {
  if (str == null) return "";
  return String(str).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#39;");
}

const CALIB_RESULTS = ["Pass", "Fail", "Adjusted"];

// Offered on the instrument department dropdown on top of the unit's own departments.
// Added here rather than to the shared department master so the Machine module's
// department list is left exactly as it is.
const EXTRA_INSTRUMENT_DEPTS = ["QUALITY"];

// The list and its calibration history are two views of the same page, the way the
// Machine List and a machine's History Card are. Opening a calibration history keeps
// the filters behind it because the list is not unmounted, only hidden.

export default function InstrumentListPage({ instruments, user, unitId, refreshInstruments, departments, units }) {
  const [calibInstrument, setCalibInstrument] = useState(null);

  // A transferred / deleted instrument must not leave an empty history on screen.
  useEffect(() => {
    if (!calibInstrument) return;
    if (!(instruments || []).some(i => i.id === calibInstrument.id)) setCalibInstrument(null);
  }, [instruments, calibInstrument]);

  const liveInstrument = calibInstrument
    ? (instruments || []).find(i => i.id === calibInstrument.id) || calibInstrument
    : null;

  return (
    <div className="space-y-4" data-testid="instrument-list-page">
      {liveInstrument ? (
        <InstrumentDetail
          instrument={liveInstrument}
          user={user}
          unitId={unitId}
          refreshInstruments={refreshInstruments}
          // Refresh the register on the way back. The list's NH badge and calibration
          // dates are counted when the list is fetched, so a list loaded before an
          // instrument gained its history goes on saying it has none — even while the
          // card you just closed showed the entries. Looking at a card is exactly the
          // moment that contradiction would be noticed, so it is also where it is fixed.
          onBack={async () => {
            setCalibInstrument(null);
            if (refreshInstruments) await refreshInstruments();
          }}
        />
      ) : (
        <InstrumentList
          instruments={instruments}
          user={user}
          unitId={unitId}
          refreshInstruments={refreshInstruments}
          departments={departments}
          units={units || []}
          onOpenCalibration={setCalibInstrument}
        />
      )}
    </div>
  );
}

function InstrumentList({ instruments, user, unitId, refreshInstruments, departments, units, onOpenCalibration }) {
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("ALL");
  const [type, setType] = useState("ALL");
  const [calib, setCalib] = useState("ALL");
  const [sortBy, setSortBy] = useState("default");

  const [showAdd, setShowAdd] = useState(false);
  const [editInstrument, setEditInstrument] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [transferInstrument, setTransferInstrument] = useState(null);
  const [transferTarget, setTransferTarget] = useState("");
  const [transferring, setTransferring] = useState(false);

  // The unit's departments, plus QUALITY — instruments live in the quality function,
  // which is not one of the production departments on the machine master — plus any
  // department already stored on an instrument, so nothing becomes unselectable.
  const deptChoices = useMemo(
    () => [...new Set([
      ...(departments || []).map(d => d.name).filter(Boolean),
      ...EXTRA_INSTRUMENT_DEPTS,
      ...(instruments || []).map(i => i.dept).filter(Boolean),
    ])],
    [departments, instruments]
  );
  const deptOptions = useMemo(() => ["ALL", ...deptChoices], [deptChoices]);
  const typeOptions = useMemo(
    () => ["ALL", ...new Set([...(instruments || []).map(i => i.instrumentType).filter(Boolean), ...INSTRUMENT_TYPES])],
    [instruments]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return (instruments || []).filter(i => {
      const matchesSearch = !q || [i.name, i.instrumentNo, i.make, i.modelSerialNo, i.instrumentType, i.rangeCapacity]
        .some(v => String(v || "").toLowerCase().includes(q));
      const matchesDept = dept === "ALL" || i.dept === dept;
      const matchesType = type === "ALL" || i.instrumentType === type;
      const matchesCalib = calib === "ALL" || calibrationState(i).key === calib;
      return matchesSearch && matchesDept && matchesType && matchesCalib;
    });
  }, [instruments, search, dept, type, calib]);

  const grouped = useMemo(
    () => filtered.reduce((a, i) => { (a[i.dept || "UNASSIGNED"] = a[i.dept || "UNASSIGNED"] || []).push(i); return a; }, {}),
    [filtered]
  );

  // Counted over the whole unit, not the filtered view — the point of the banner is to
  // show what is outstanding even while you are looking at one department.
  // Instruments whose History Card is empty — badged NH on the row.
  const noHistoryCount = useMemo(
    () => filtered.filter(i => i.historyCount === 0).length, [filtered]);

  const dueSummary = useMemo(() => {
    let overdue = 0, due = 0;
    for (const i of instruments || []) {
      const s = calibrationState(i).key;
      if (s === "overdue") overdue++;
      else if (s === "due") due++;
    }
    return { overdue, due };
  }, [instruments]);

  function sortInstruments(list) {
    if (sortBy === "default") return list;
    const dueTime = i => {
      const d = i.calibrationDueDate ? new Date(i.calibrationDueDate) : null;
      return d && !isNaN(d.getTime()) ? d.getTime() : Infinity;   // undated sort last either way
    };
    return [...list].sort((a, b) => {
      if (sortBy === "due_asc") return dueTime(a) - dueTime(b);
      if (sortBy === "due_desc") {
        const [x, y] = [dueTime(a), dueTime(b)];
        if (x === Infinity) return 1;
        if (y === Infinity) return -1;
        return y - x;
      }
      if (sortBy === "name_asc") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "type_asc") return (a.instrumentType || "").localeCompare(b.instrumentType || "");
      if (sortBy === "make_asc") return (a.make || "").localeCompare(b.make || "");
      return 0;
    });
  }

  const otherUnits = useMemo(() => (units || []).filter(u => u.id !== unitId), [units, unitId]);
  const currentUnitName = (units || []).find(u => u.id === unitId)?.shortName || unitId;

  const blankForm = {
    name: "", instrumentNo: "", instrumentType: "", make: "", modelSerialNo: "", rangeCapacity: "",
    dept: "", calibrationFrequency: "", lastCalibrationDate: "", calibrationDueDate: "",
    calibrationStatus: "Not Calibrated", status: "Active", remarks: "",
    leastCount: "", issuedTo: "", calibrationLab: "", calibrationSource: "",
    purchaseMonthYear: "", manualAvailable: "",
  };

  function startEdit(i) { setForm({ ...i }); setEditInstrument(i.id); setShowAdd(false); }
  function startAdd() { setForm({ ...blankForm }); setShowAdd(true); setEditInstrument(null); }

  function validate() {
    if (!String(form.name || "").trim()) { alert("Please enter the Instrument Name."); return false; }
    if (!String(form.instrumentNo || "").trim()) { alert("Please enter the Instrument No. / ID."); return false; }
    if (!String(form.dept || "").trim()) { alert("Please select the Department."); return false; }
    return true;
  }

  async function saveEdit() {
    if (!validate()) return;
    setSaving(true);
    try { await apiCall("PUT", `/instruments/${unitId}/${form.id}`, form); await refreshInstruments(); setEditInstrument(null); }
    catch (error) { alert(error.response?.data?.detail || "Failed to save instrument"); }
    finally { setSaving(false); }
  }

  async function saveAdd() {
    if (!validate()) return;
    setSaving(true);
    try { await apiCall("POST", `/instruments/${unitId}`, form); await refreshInstruments(); setShowAdd(false); }
    catch (error) { alert(error.response?.data?.detail || "Failed to add instrument"); }
    finally { setSaving(false); }
  }

  async function deleteInstrument(i) {
    if (!window.confirm(`Delete ${i.name} (${i.instrumentNo})? Its calibration and maintenance records will also be removed.`)) return;
    try { await apiCall("DELETE", `/instruments/${unitId}/${i.id}`); await refreshInstruments(); }
    catch (error) { alert(error.response?.data?.detail || "Failed to delete instrument"); }
  }

  async function doTransfer() {
    if (!transferInstrument || !transferTarget) return;
    const targetName = (units || []).find(u => u.id === transferTarget)?.shortName || transferTarget;
    if (!window.confirm(`Transfer ${transferInstrument.name} from ${currentUnitName} to ${targetName}? Its calibration history moves with it.`)) return;
    setTransferring(true);
    try {
      await apiCall("POST", `/instruments/${unitId}/${transferInstrument.id}/transfer`, { target_unit_id: transferTarget });
      alert(`${transferInstrument.name} transferred to ${targetName} successfully.`);
      setTransferInstrument(null);
      setTransferTarget("");
      await refreshInstruments();
    } catch (error) {
      alert(error.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  }

  function handleExportCSV() {
    const headers = ["#", "Instrument Name", "Instrument No.", "Type", "Make", "Model / Serial No.",
      "Range / Capacity", "Department", "Calibration Frequency", "Last Calibrated",
      "Calibration Due", "Calibration Status", "Status", "Remarks"];
    const cell = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((i, n) => [
      n + 1, i.name, i.instrumentNo, i.instrumentType, i.make, i.modelSerialNo, i.rangeCapacity,
      i.dept, i.calibrationFrequency, i.lastCalibrationDate, i.calibrationDueDate,
      calibrationState(i).label, i.status, i.remarks,
    ].map(cell).join(","));
    const csv = [headers.map(cell).join(","), ...rows].join("\r\n");
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `Instrument_List_${unitId}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function handleExportPDF() {
    setExporting(true);
    try {
      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2,"0")}-${String(now.getMonth()+1).padStart(2,"0")}-${now.getFullYear()}`;
      const allGrouped = (instruments || []).reduce((a, i) => { (a[i.dept || "UNASSIGNED"] = a[i.dept || "UNASSIGNED"] || []).push(i); return a; }, {});

      const calibColor = (key) => key === "overdue" ? "color:#dc2626" : key === "due" ? "color:#d97706" : key === "ok" ? "color:#16a34a" : "color:#6b7280";
      const th = 'style="border:1px solid #ddd;padding:4px 8px;font-size:10px;background:#f8f8f8;font-weight:bold;text-align:left"';
      const td = 'style="border:1px solid #ddd;padding:4px 8px;font-size:10px"';

      let html = `<div style="font-family:Arial,sans-serif">`;
      html += `<div style="font-size:16px;font-weight:bold;margin-bottom:4px">Instrument List &mdash; ${escapeHtml(currentUnitName)}</div>`;
      html += `<div style="font-size:11px;color:#666;margin-bottom:10px">Calibration Master List | Generated: ${dateStr} | Total: ${(instruments || []).length} instruments | Overdue: ${dueSummary.overdue} | Due soon: ${dueSummary.due}</div>`;

      for (const [deptName, iList] of Object.entries(allGrouped)) {
        html += `<div style="background:#f0f0f0;font-weight:bold;font-size:12px;padding:6px 8px;margin-top:14px;border:1px solid #ddd">${escapeHtml(deptName)} (${iList.length})</div>`;
        html += `<table style="border-collapse:collapse;width:100%"><thead><tr>`;
        for (const h of ["#","Instrument Name","Instrument No.","Type","Make","Model / Serial","Range","Last Calibrated","Calibration Due","Calibration","Status"]) html += `<th ${th}>${h}</th>`;
        html += `</tr></thead><tbody>`;
        iList.forEach((i, n) => {
          const cs = calibrationState(i);
          html += `<tr>`;
          html += `<td ${td}>${n+1}</td>`;
          html += `<td ${td}>${escapeHtml(i.name)}</td>`;
          html += `<td ${td}>${escapeHtml(i.instrumentNo)}</td>`;
          html += `<td ${td}>${escapeHtml(i.instrumentType)}</td>`;
          html += `<td ${td}>${escapeHtml(i.make)}</td>`;
          html += `<td ${td}>${escapeHtml(i.modelSerialNo)}</td>`;
          html += `<td ${td}>${escapeHtml(i.rangeCapacity)}</td>`;
          html += `<td ${td}>${escapeHtml(i.lastCalibrationDate)}</td>`;
          html += `<td ${td}>${escapeHtml(i.calibrationDueDate)}</td>`;
          html += `<td ${td} style="border:1px solid #ddd;padding:4px 8px;font-size:10px;${calibColor(cs.key)};font-weight:600">${escapeHtml(cs.label)}</td>`;
          html += `<td ${td}>${escapeHtml(i.status)}</td>`;
          html += `</tr>`;
        });
        html += `</tbody></table>`;
      }
      html += `</div>`;

      const div = document.createElement("div");
      div.innerHTML = html;
      document.body.appendChild(div);

      const opt = {
        margin: 5,
        filename: `Instrument_List_${unitId}_${new Date().toISOString().split("T")[0]}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      };
      await html2pdf().set(opt).from(div).save();
      document.body.removeChild(div);
    } catch (error) {
      alert("PDF export failed: " + (error.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  }

  const InstrumentForm = ({ onSave, onCancel, title }) => (
    <Modal title={title} onClose={onCancel}>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Instrument Name *" value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Vernier Caliper" />
        <Input label="Instrument No. / ID *" value={form.instrumentNo || ""} onChange={e => setForm(f => ({ ...f, instrumentNo: e.target.value }))} placeholder="e.g. INS-VC-01" />
        <Select label="Instrument Type" value={form.instrumentType || ""} onChange={e => setForm(f => ({ ...f, instrumentType: e.target.value }))} options={[{ value: "", label: "-- Select Type --" }, ...INSTRUMENT_TYPES.map(t => ({ value: t, label: t }))]} />
        <Input label="Make / Manufacturer" value={form.make || ""} onChange={e => setForm(f => ({ ...f, make: e.target.value }))} placeholder="e.g. Mitutoyo" />
        <Input label="Model / Serial No." value={form.modelSerialNo || ""} onChange={e => setForm(f => ({ ...f, modelSerialNo: e.target.value }))} />
        <Input label="Range / Capacity" value={form.rangeCapacity || ""} onChange={e => setForm(f => ({ ...f, rangeCapacity: e.target.value }))} placeholder="e.g. 0-150 MM" />
        <Select label="Department *" value={form.dept || ""} onChange={e => setForm(f => ({ ...f, dept: e.target.value }))} options={[{ value: "", label: "-- Select Department --" }, ...deptChoices.map(d => ({ value: d, label: d }))]} />
        <Input label="Calibration Frequency" value={form.calibrationFrequency || ""} onChange={e => setForm(f => ({ ...f, calibrationFrequency: e.target.value }))} placeholder="e.g. 1 YEAR" />
        {/* Asked for by the History Card (QA-F-28) header, so they need somewhere to be
            entered — otherwise the card prints with two fields permanently blank. */}
        <Input label="Least Count" value={form.leastCount || ""} onChange={e => setForm(f => ({ ...f, leastCount: e.target.value }))} placeholder="e.g. 0.01 MM" />
        <Input label="Issued To" value={form.issuedTo || ""} onChange={e => setForm(f => ({ ...f, issuedTo: e.target.value }))} placeholder="Who holds it" />
        <Input label="Calibration Lab / Agency" value={form.calibrationLab || ""} onChange={e => setForm(f => ({ ...f, calibrationLab: e.target.value }))} placeholder="e.g. ADVANCE" />
        <Input label="Calibration Source" value={form.calibrationSource || ""} onChange={e => setForm(f => ({ ...f, calibrationSource: e.target.value }))} placeholder="e.g. OUT SOURCE" />
        <Input label="Month / Year of Purchase" value={form.purchaseMonthYear || ""} onChange={e => setForm(f => ({ ...f, purchaseMonthYear: e.target.value }))} placeholder="e.g. JUL 2020" />
        <Select label="Instrument Manual Available" value={form.manualAvailable || ""} onChange={e => setForm(f => ({ ...f, manualAvailable: e.target.value }))} options={[{ value: "", label: "—" }, { value: "Yes", label: "Yes" }, { value: "No", label: "No" }]} />
        <Input label="Last Calibration Date" type="date" value={form.lastCalibrationDate || ""} onChange={e => setForm(f => ({ ...f, lastCalibrationDate: e.target.value }))} />
        <Input label="Calibration Due Date" type="date" value={form.calibrationDueDate || ""} onChange={e => setForm(f => ({ ...f, calibrationDueDate: e.target.value }))} />
        <Select label="Calibration Status" value={form.calibrationStatus || "Not Calibrated"} onChange={e => setForm(f => ({ ...f, calibrationStatus: e.target.value }))} options={CALIBRATION_STATUSES} />
        <Select label="Current Status" value={form.status || "Active"} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} options={STATUSES_I} />
        <Input label="Remarks" value={form.remarks || ""} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
      </div>
      <p className="text-xs text-gray-400 mt-3">
        Calibration Status and the due date are updated for you whenever a calibration record is added under Calibration History.
      </p>
      <div className="flex gap-2 mt-4">
        <Btn onClick={onSave} disabled={saving} testId="save-instrument-btn">{saving ? "Saving..." : "Save Changes"}</Btn>
        <Btn onClick={onCancel} variant="ghost">Cancel</Btn>
      </div>
    </Modal>
  );

  return (
    <div className="space-y-4" data-testid="instrument-list">
      {editInstrument && InstrumentForm({ title: "Edit Instrument", onSave: saveEdit, onCancel: () => setEditInstrument(null) })}
      {showAdd && InstrumentForm({ title: "Add New Instrument", onSave: saveAdd, onCancel: () => setShowAdd(false) })}
      {transferInstrument && (
        <Modal title="Transfer Instrument" onClose={() => { setTransferInstrument(null); setTransferTarget(""); }}>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 font-medium">Instrument</label>
              <div className="mt-0.5 px-3 py-2 bg-gray-50 border rounded-lg text-sm font-medium text-gray-800" data-testid="transfer-instrument-name">
                {transferInstrument.name} <span className="text-xs font-mono text-gray-500 ml-1">({transferInstrument.instrumentNo})</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">From</label>
              <div className="mt-0.5 px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-600">{currentUnitName}</div>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-0.5">Transfer to</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={transferTarget}
                onChange={e => setTransferTarget(e.target.value)}
                data-testid="transfer-instrument-unit-select"
              >
                <option value="">-- Select target unit --</option>
                {otherUnits.map(u => <option key={u.id} value={u.id}>{u.shortName || u.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Btn onClick={doTransfer} disabled={transferring || !transferTarget} testId="confirm-instrument-transfer-btn">
              {transferring ? "Transferring..." : "Transfer"}
            </Btn>
            <Btn onClick={() => { setTransferInstrument(null); setTransferTarget(""); }} variant="ghost">Cancel</Btn>
          </div>
        </Modal>
      )}

      <div className="flex flex-col sm:flex-row gap-2 justify-between">
        <div className="flex gap-2 flex-1 flex-wrap">
          <input className="flex-1 min-w-[180px] border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" placeholder="Search by name, number, make, serial..." value={search} onChange={e => setSearch(e.target.value)} data-testid="instrument-search" />
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={dept} onChange={e => setDept(e.target.value)} data-testid="instrument-dept-filter">
            {deptOptions.map(d => <option key={d}>{d}</option>)}
          </select>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={type} onChange={e => setType(e.target.value)} data-testid="instrument-type-filter">
            {typeOptions.map(t => <option key={t}>{t}</option>)}
          </select>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={calib} onChange={e => setCalib(e.target.value)} data-testid="instrument-calib-filter">
            <option value="ALL">Calibration: All</option>
            <option value="overdue">Overdue</option>
            <option value="due">Due Soon</option>
            <option value="ok">Calibrated</option>
            <option value="unknown">No Due Date</option>
            <option value="none">Not Required</option>
          </select>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="instrument-sort-select">
            <option value="default">Sort: Default</option>
            <option value="due_asc">Due Date: Soonest First</option>
            <option value="due_desc">Due Date: Latest First</option>
            <option value="name_asc">Name: A to Z</option>
            <option value="type_asc">Type: A to Z</option>
            <option value="make_asc">Make: A to Z</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportCSV}
            className="border border-green-600 text-green-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-50 whitespace-nowrap"
            title="Downloads a .csv file — Excel opens it directly"
            data-testid="export-instruments-csv-btn">Export Excel</button>
          <button onClick={handleExportPDF} disabled={exporting}
            className="border border-blue-600 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-50 disabled:opacity-50 whitespace-nowrap"
            data-testid="export-instruments-pdf-btn">
            {exporting ? "Exporting..." : "Export PDF"}
          </button>
          {can(user, "addMachines") && (
            <button onClick={startAdd}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-700 whitespace-nowrap"
              data-testid="add-instrument-btn">+ Add Instrument</button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-gray-400">{filtered.length} instruments &middot; Calibration Master List &middot; {currentUnitName}</div>
        <div className="flex gap-2">
          {dueSummary.overdue > 0 && (
            <span className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-3 py-1" data-testid="calib-overdue-summary"
              title="Calibration due date has passed. Open Calibration History to record the new certificate.">
              {dueSummary.overdue} calibration {dueSummary.overdue === 1 ? "overdue" : "overdue"}
            </span>
          )}
          {dueSummary.due > 0 && (
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="calib-due-summary"
              title="Calibration falls due within the next 30 days.">
              {dueSummary.due} due within 30 days
            </span>
          )}
          {noHistoryCount > 0 && (
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="no-history-summary"
              title="Marked NH in the list. Nothing is recorded on their History Cards — no certificate, no calibration, nothing.">
              {noHistoryCount} with no history
            </span>
          )}
        </div>
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="bg-white rounded-xl border p-8 text-center text-sm text-gray-400" data-testid="instrument-empty">
          {(instruments || []).length === 0
            ? "No instruments in this unit yet. Use + Add Instrument to start the list."
            : "No instruments match these filters."}
        </div>
      )}

      {Object.entries(grouped).map(([deptName, iList]) => (
        <div key={deptName} className="bg-white rounded-xl border overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 flex justify-between">
            <h3 className="font-semibold text-gray-700 text-sm">{deptName}</h3>
            <span className="text-xs text-gray-400">{iList.length} instruments</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b">{["#", "Instrument Name", "Instrument No.", "Type", "Make", "Model / Serial", "Range", "Calibration Due", "Calibration", "Status", ""].map(h => <th key={h} className="text-left px-3 py-2 text-xs text-gray-500 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {sortInstruments(iList).map((i, n) => {
                  const cs = calibrationState(i);
                  return (
                    <tr key={i.id} className="border-b hover:bg-blue-50 transition-colors">
                      <td className="px-3 py-2 text-gray-400">{n + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-800 cursor-pointer hover:text-blue-600" onClick={() => onOpenCalibration(i)} data-testid={`instrument-name-${i.id}`}>
                        {i.name}
                        {/* Nothing on its History Card. Not the same as being overdue — this
                            one has no record of ever having been calibrated at all.
                            Tested against 0 rather than falsiness: a list loaded before the
                            count existed has no historyCount at all, and claiming "no
                            history" for an instrument we simply have not counted puts the
                            badge on every row, including ones with a full card. */}
                        {i.historyCount === 0 && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 align-middle"
                            title="No history — nothing recorded on this instrument's History Card yet"
                            data-testid={`no-history-${i.id}`}>NH</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-blue-600 text-xs">{i.instrumentNo}</td>
                      <td className="px-3 py-2 text-gray-600">{i.instrumentType || "—"}</td>
                      <td className="px-3 py-2 text-gray-600">{i.make || "—"}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{i.modelSerialNo || "—"}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{i.rangeCapacity || "—"}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{i.calibrationDueDate || "—"}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${cs.cls}`} title={i.lastCalibrationDate ? `Last calibrated ${i.lastCalibrationDate}` : "No calibration recorded"}>{cs.label}</span></td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${i.status === "Active" ? "bg-green-100 text-green-700" : i.status === "Under Calibration" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>{i.status}</span></td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 whitespace-nowrap">
                          <button onClick={() => onOpenCalibration(i)} className="text-blue-400 hover:text-blue-600 text-xs" data-testid={`calibration-btn-${i.id}`}>History</button>
                          {can(user, "editMachines") && <><button onClick={() => startEdit(i)} className="text-gray-400 hover:text-gray-600 text-xs ml-1" data-testid={`instrument-edit-btn-${i.id}`}>Edit</button><button onClick={() => deleteInstrument(i)} className="text-red-300 hover:text-red-500 text-xs ml-1" data-testid={`instrument-delete-btn-${i.id}`}>Del</button></>}
                          {user?.role === "superadmin" && <button onClick={() => { setTransferInstrument(i); setTransferTarget(""); }} className="text-purple-400 hover:text-purple-600 text-xs ml-1" data-testid={`instrument-transfer-btn-${i.id}`}>Transfer</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

const BLANK_CALIB = {
  calibrationDate: "", nextDueDate: "", calibratedBy: "", certificateNo: "",
  result: "Pass", remarks: "", certificateFilePath: null, certificateFileName: "",
};

// The instrument's own page: its details, then its two record books — calibration
// certificates and maintenance work — as tabs, the way the Machine List separates the
// list from its revision history.
function InstrumentDetail({ instrument, user, unitId, refreshInstruments, onBack }) {
  const [tab, setTab] = useState("card");
  const cs = calibrationState(instrument);

  // Both record books are loaded here rather than inside their tabs. The header reports
  // the last calibration and the last maintenance whichever tab is open, and because the
  // tabs add and delete through these same loaders, those dates move the moment a record
  // is written — no reopening the page.
  const [calibRecords, setCalibRecords] = useState([]);
  const [calibLoading, setCalibLoading] = useState(true);
  const [maintRecords, setMaintRecords] = useState([]);
  const [maintLoading, setMaintLoading] = useState(true);

  const loadCalibrations = useCallback(async () => {
    setCalibLoading(true);
    try { setCalibRecords(await apiCall("GET", `/instruments/${unitId}/${instrument.id}/calibrations`)); }
    catch (error) { setCalibRecords([]); }
    finally { setCalibLoading(false); }
  }, [unitId, instrument.id]);

  const loadMaintenance = useCallback(async () => {
    setMaintLoading(true);
    try { setMaintRecords(await apiCall("GET", `/instruments/${unitId}/${instrument.id}/maintenance`)); }
    catch (error) { setMaintRecords([]); }
    finally { setMaintLoading(false); }
  }, [unitId, instrument.id]);

  useEffect(() => { loadCalibrations(); loadMaintenance(); }, [loadCalibrations, loadMaintenance]);

  // Both lists come back newest first, but an entry can be back-dated — read the latest
  // date held rather than trusting the position in the list.
  const latestDate = (rows, key) => {
    const dates = (rows || []).map(r => r[key]).filter(Boolean);
    return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : "";
  };
  const lastMaintenanceDate = useMemo(() => latestDate(maintRecords, "startDate"), [maintRecords]);
  // The certificates are the source of truth; the date stored on the instrument is the
  // fallback for one that was typed into the form before any certificate was filed.
  const lastCalibrationDate = useMemo(
    () => latestDate(calibRecords, "calibrationDate") || instrument.lastCalibrationDate || "",
    [calibRecords, instrument.lastCalibrationDate]
  );

  const basics = [
    ["Instrument Type", instrument.instrumentType],
    ["Make", instrument.make],
    ["Model / Serial No.", instrument.modelSerialNo],
    ["Range / Capacity", instrument.rangeCapacity],
    ["Department", instrument.dept],
    ["Current Status", instrument.status],
  ];
  const dates = [
    ["Last Calibration Done On", calibLoading ? "…" : lastCalibrationDate],
    ["Next Calibration Due On", instrument.calibrationDueDate],
    ["Last Maintenance Done On", maintLoading ? "…" : lastMaintenanceDate],
  ];

  // Built to the same shape as the machine History Card: back link, the actions, then
  // the card itself. The summary boxes and the three tabs that used to sit above it said
  // the same things the card's own header already says, twice over.
  return (
    <div className="space-y-4" data-testid="instrument-detail">
      <InstrumentHistoryCard
        instrument={instrument}
        user={user}
        unitId={unitId}
        refreshInstruments={async () => {
          if (refreshInstruments) await refreshInstruments();
          await loadCalibrations();
          await loadMaintenance();
        }}
        onBack={onBack}
      />
    </div>
  );
}

// Records are owned by InstrumentDetail — the header reads the same list for its
// "Last Calibration Done On", so both stay in step off one fetch.
function CalibrationHistory({ instrument, user, unitId, refreshInstruments, records = [], loading = false, reload }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...BLANK_CALIB });
  const [saving, setSaving] = useState(false);

  const load = reload;

  const todayStr = new Date().toISOString().split("T")[0];

  async function saveRecord() {
    if (!form.calibrationDate) { alert("Please enter the Calibration Date."); return; }
    setSaving(true);
    try {
      await apiCall("POST", `/instruments/${unitId}/${instrument.id}/calibrations`, form);
      setShowAdd(false);
      setForm({ ...BLANK_CALIB });
      await load();
      // The instrument row carries the due date / status the backend just moved.
      await refreshInstruments();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to save calibration record");
    } finally { setSaving(false); }
  }

  async function deleteRecord(r) {
    if (!window.confirm(`Delete the calibration record dated ${r.calibrationDate}?`)) return;
    try {
      await apiCall("DELETE", `/instruments/${unitId}/${instrument.id}/calibrations/${r.id}`);
      await load();
      await refreshInstruments();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to delete calibration record");
    }
  }

  return (
    <div className="space-y-4" data-testid="calibration-history">
      {showAdd && (
        <Modal title="Add Calibration Record" onClose={() => setShowAdd(false)}>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Calibration Date *" type="date" value={form.calibrationDate} onChange={e => setForm(f => ({ ...f, calibrationDate: e.target.value }))} />
            <Input label="Next Due Date" type="date" value={form.nextDueDate} onChange={e => setForm(f => ({ ...f, nextDueDate: e.target.value }))} />
            <Input label="Calibrated By / Agency" value={form.calibratedBy} onChange={e => setForm(f => ({ ...f, calibratedBy: e.target.value }))} placeholder="e.g. NABL Lab / In-house" />
            <Input label="Certificate No." value={form.certificateNo} onChange={e => setForm(f => ({ ...f, certificateNo: e.target.value }))} />
            <Select label="Result" value={form.result} onChange={e => setForm(f => ({ ...f, result: e.target.value }))} options={CALIB_RESULTS} />
            <Input label="Remarks" value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
          </div>
          <div className="mt-3">
            <FileUploadBox
              label="Calibration Certificate"
              file={form.certificateFilePath}
              fileName={form.certificateFileName}
              onChange={file => setForm(f => ({ ...f, certificateFilePath: file?.path || null, certificateFileName: file?.name || "" }))}
              onView={form.certificateFilePath ? () => openProtectedFile({ preventDefault() {} }, form.certificateFilePath, form.certificateFileName) : null}
              accept=".pdf,.jpg,.jpeg,.png"
              hint="Upload the calibration certificate PDF / scan"
            />
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Saving a calibration dated {form.calibrationDate || todayStr} updates the instrument's Last Calibrated date, and its due date when a Next Due Date is given.
          </p>
          <div className="flex gap-2 mt-4">
            <Btn onClick={saveRecord} disabled={saving} testId="save-calibration-btn">{saving ? "Saving..." : "Save Record"}</Btn>
            <Btn onClick={() => setShowAdd(false)} variant="ghost">Cancel</Btn>
          </div>
        </Modal>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 flex justify-between items-center gap-3">
          <h3 className="font-semibold text-gray-700 text-sm">Calibration History</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">{records.length} {records.length === 1 ? "record" : "records"}</span>
            {can(user, "addHistory") && <Btn onClick={() => { setForm({ ...BLANK_CALIB }); setShowAdd(true); }} testId="add-calibration-btn">+ Add Calibration Record</Btn>}
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading calibration history...</div>
        ) : records.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400" data-testid="calibration-empty">
            No calibration recorded for this instrument yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b">{["#", "Calibration Date", "Next Due", "Calibrated By / Agency", "Certificate No.", "Result", "Certificate", "Remarks", "Recorded By", ""].map(h => <th key={h} className="text-left px-3 py-2 text-xs text-gray-500 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {records.map((r, n) => (
                  <tr key={r.id} className="border-b hover:bg-blue-50 transition-colors" data-testid={`calibration-row-${r.id}`}>
                    <td className="px-3 py-2 text-gray-400">{n + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{r.calibrationDate || "—"}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.nextDueDate || "—"}</td>
                    <td className="px-3 py-2 text-gray-600">{r.calibratedBy || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{r.certificateNo || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${r.result === "Pass" ? "bg-green-100 text-green-700" : r.result === "Fail" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{r.result || "—"}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.certificateFilePath ? (
                        <button onClick={(ev) => openProtectedFile(ev, r.certificateFilePath, r.certificateFileName)} className="text-blue-500 hover:text-blue-700 underline" data-testid={`certificate-link-${r.id}`}>
                          {r.certificateFileName || "View"}
                        </button>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{r.remarks || "—"}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs">{r.recordedBy || "—"}</td>
                    <td className="px-3 py-2">
                      {can(user, "deleteHistory") && <button onClick={() => deleteRecord(r)} className="text-red-300 hover:text-red-500 text-xs" data-testid={`calibration-delete-${r.id}`}>Del</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// A maintenance entry on an instrument, kept in the same shape as a machine's
// maintenance log so the two read alike: the same work types, the same maintenance-type
// classification, the same vendor / PO block, and the same open-until-closed lifecycle.
const BLANK_MAINT = {
  startDate: "", startTime: "", closeNow: false,
  description: "", maintenanceType: "", detail: "", rootCause: "", rectification: "",
  sparePart: "", supervisor: "", workType: "internal",
  vendorName: "", poNumber: "", poDate: "", poAmount: "", paymentMode: "po",
  reportFilePath: null, reportFileName: "",
  beforeReportFilePath: null, beforeReportFileName: "",
  poFilePath: null, poFileName: "",
};

// Records are owned by InstrumentDetail — the header reads the same list for its
// "Last Maintenance Done On", so both stay in step off one fetch.
function MaintenanceHistory({ instrument, user, unitId, records = [], loading = false, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...BLANK_MAINT });
  const [saving, setSaving] = useState(false);

  const load = reload;

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const todayStr = new Date().toISOString().split("T")[0];
  const minDateStr = "2000-01-01";

  const isExternal = form.workType === "external";
  // Same rule as the machine log: internal work asks for purchase details only once a
  // spare part is involved; external work always does.
  const hasSparePart = !!String(form.sparePart || "").trim();
  const poBlockShown = isExternal || hasSparePart;
  const paidInCash = form.paymentMode === "cash";

  // An entry already closed keeps its close stamp — only an open one can be closed here.
  const editingClosed = !!(editId && String(form.endDate || "").trim());

  function startAdd() { setForm({ ...BLANK_MAINT, startDate: todayStr }); setEditId(null); setShowForm(true); }
  function startEdit(r) {
    setForm({
      ...BLANK_MAINT,
      ...r,
      closeNow: false,
      reportFilePath: r.reportFilePath || null,
      beforeReportFilePath: r.beforeReportFilePath || null,
      poFilePath: r.poFilePath || null,
    });
    setEditId(r.id);
    setShowForm(true);
  }

  function validate() {
    if (!form.startDate) { alert("Please enter the Date."); return false; }
    if (!String(form.description || "").trim()) { alert("Please enter the Description of Incident / Event."); return false; }
    if (!String(form.supervisor || "").trim()) { alert("Please enter the Sign. Maint. Supervisor."); return false; }
    if (poBlockShown) {
      if (!String(form.vendorName || "").trim()) { alert("Please enter the Vendor / Agency Name."); return false; }
      if (!paidInCash && !String(form.poNumber || "").trim()) { alert("Please enter the Purchase Order No."); return false; }
      if (!paidInCash && !String(form.poDate || "").trim()) { alert("Please enter the PO Date."); return false; }
      if (!(Number(form.poAmount) > 0)) { alert(`Please enter a valid ${paidInCash ? "Cash Amount" : "PO Amount"}.`); return false; }
    }
    return true;
  }

  async function saveRecord() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, poAmount: String(form.poAmount ?? "") };
      delete payload.id;
      if (editId) await apiCall("PUT", `/instruments/${unitId}/${instrument.id}/maintenance/${editId}`, payload);
      else await apiCall("POST", `/instruments/${unitId}/${instrument.id}/maintenance`, payload);
      setShowForm(false);
      setEditId(null);
      setForm({ ...BLANK_MAINT });
      await load();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to save maintenance record");
    } finally { setSaving(false); }
  }

  async function deleteRecord(r) {
    if (!window.confirm(`Delete the maintenance record dated ${r.startDate}?`)) return;
    try {
      await apiCall("DELETE", `/instruments/${unitId}/${instrument.id}/maintenance/${r.id}`);
      await load();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to delete maintenance record");
    }
  }

  const openCount = records.filter(r => !String(r.endDate || "").trim()).length;

  return (
    <div className="space-y-4" data-testid="maintenance-history">
      {showForm && (
        <Modal title={editId ? "Edit Maintenance Record" : "Add Maintenance Record"} onClose={() => { setShowForm(false); setEditId(null); }} size="wide">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[{ val: "internal", title: "Internal Work", desc: "Done by in-house maintenance team." },
                { val: "external", title: "External Work", desc: "Done by outside vendor / agency." }].map(opt => (
                <button key={opt.val} type="button" onClick={() => f("workType", opt.val)}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${form.workType === opt.val ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                  data-testid={`instrument-work-type-${opt.val}`}>
                  <div className={`font-semibold text-sm ${form.workType === opt.val ? "text-blue-700" : "text-gray-800"}`}>{opt.title}</div>
                  <div className="text-xs text-gray-500 leading-relaxed">{opt.desc}</div>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500">Date *</label><input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" min={minDateStr} max={todayStr} value={form.startDate || ""} onChange={e => f("startDate", e.target.value)} data-testid="instrument-maint-date" /></div>
              <div><label className="text-xs text-gray-500">Time</label><TimeField value={form.startTime || ""} onChange={v => f("startTime", v)} /></div>
              <div><label className="text-xs text-gray-500">Description of Incident / Event *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="e.g. Display not working, Scheduled service" value={form.description || ""} onChange={e => f("description", e.target.value)} data-testid="instrument-maint-description" /></div>
              <div>
                <label className="text-xs text-gray-500">Maintenance Type</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5 bg-white"
                  value={form.maintenanceType || ""} onChange={e => f("maintenanceType", e.target.value)} data-testid="instrument-maint-type">
                  <option value="">Auto &mdash; {MT_LABELS[classifyMaintenanceType(form.description)]}</option>
                  {MT_OPTIONS.map(t => <option key={t} value={t}>{MT_LABELS[t]}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-0.5">Auto reads the description; pick a type to override it.</p>
              </div>
              <div><label className="text-xs text-gray-500">Detail of Incident / Event</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="Describe the problem in detail" value={form.detail || ""} onChange={e => f("detail", e.target.value)} /></div>
              <div><label className="text-xs text-gray-500">Root Cause</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="What caused this?" value={form.rootCause || ""} onChange={e => f("rootCause", e.target.value)} /></div>
              <div><label className="text-xs text-gray-500">Rectification / Maintenance Detail</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="What was done?" value={form.rectification || ""} onChange={e => f("rectification", e.target.value)} /></div>
              <div><label className="text-xs text-gray-500">Spare Part Used (if any)</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="Part name / size" value={form.sparePart || ""} onChange={e => f("sparePart", e.target.value)} data-testid="instrument-maint-spare" /></div>
              <div><label className="text-xs text-gray-500">Sign. Maint. Supervisor *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="Supervisor name" value={form.supervisor || ""} onChange={e => f("supervisor", e.target.value)} data-testid="instrument-maint-supervisor" /></div>
            </div>

            {editingClosed ? (
              <div className="text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700">
                Closed on <span className="font-semibold">{form.endDate}{form.endTime ? ` ${form.endTime}` : ""}</span>
              </div>
            ) : (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={!!form.closeNow} onChange={e => f("closeNow", e.target.checked)} data-testid="instrument-maint-close-now" />
                Mark this entry as <span className="font-semibold">Closed</span> (stamped with the current date and time)
              </label>
            )}

            {poBlockShown && (
              <div className={`${isExternal ? "bg-orange-50 border-orange-200" : "bg-blue-50 border-blue-200"} border rounded-xl p-4 space-y-3`} data-testid="instrument-po-section">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${isExternal ? "text-orange-700" : "text-blue-700"}`}>
                    {isExternal ? "External Vendor Details" : paidInCash ? "Cash Purchase Details" : "Purchase Order Details"}
                  </p>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                    {["po", "cash"].map(val => (
                      <button key={val} type="button" onClick={() => f("paymentMode", val)}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${form.paymentMode === val ? "bg-white text-gray-900 border-gray-400 shadow-sm" : "border-transparent text-gray-500 hover:bg-white/60"}`}
                        data-testid={`instrument-payment-mode-${val}`}>{val === "po" ? "Purchase Order" : "Cash"}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-gray-500">Vendor / Agency Name *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="e.g. Impress Engineering" value={form.vendorName || ""} onChange={e => f("vendorName", e.target.value)} data-testid="instrument-po-vendor" /></div>
                  {!paidInCash && <div><label className="text-xs text-gray-500">Purchase Order No. *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="e.g. PO-2025-0142" value={form.poNumber || ""} onChange={e => f("poNumber", e.target.value)} data-testid="instrument-po-number" /></div>}
                  {!paidInCash && <div><label className="text-xs text-gray-500">PO Date *</label><input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" min={minDateStr} max={todayStr} value={form.poDate || ""} onChange={e => f("poDate", e.target.value)} data-testid="instrument-po-date" /></div>}
                  <div><label className="text-xs text-gray-500">{paidInCash ? "Cash Amount" : "PO Amount"} *</label><input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="0.00" value={form.poAmount || ""} onChange={e => f("poAmount", e.target.value)} data-testid="instrument-po-amount" /></div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FileUploadBox label="Before Maintenance — Report / Evidence" file={form.beforeReportFilePath} fileName={form.beforeReportFileName}
                onChange={file => setForm(p => ({ ...p, beforeReportFilePath: file?.path || null, beforeReportFileName: file?.name || "" }))}
                onView={form.beforeReportFilePath ? () => openProtectedFile({ preventDefault() {} }, form.beforeReportFilePath, form.beforeReportFileName) : null}
                accept=".pdf,.jpg,.jpeg,.png" hint="Photo / PDF of the instrument condition BEFORE the work" />
              <FileUploadBox label="After Maintenance — Report / Evidence (Work Done)" file={form.reportFilePath} fileName={form.reportFileName}
                onChange={file => setForm(p => ({ ...p, reportFilePath: file?.path || null, reportFileName: file?.name || "" }))}
                onView={form.reportFilePath ? () => openProtectedFile({ preventDefault() {} }, form.reportFilePath, form.reportFileName) : null}
                accept=".pdf,.jpg,.jpeg,.png" hint="Photo / PDF of the work-done report" />
              {poBlockShown && (
                <FileUploadBox label={paidInCash ? "Cash Bill / Receipt" : "Purchase Order (PO) Document"} file={form.poFilePath} fileName={form.poFileName}
                  onChange={file => setForm(p => ({ ...p, poFilePath: file?.path || null, poFileName: file?.name || "" }))}
                  onView={form.poFilePath ? () => openProtectedFile({ preventDefault() {} }, form.poFilePath, form.poFileName) : null}
                  accept=".pdf,.jpg,.jpeg,.png" hint={paidInCash ? "Attach the cash bill or receipt" : "Upload the Purchase Order PDF / scan"} />
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Btn onClick={saveRecord} disabled={saving} testId="save-instrument-maint-btn">{saving ? "Saving..." : editId ? "Update Record" : "Save Record"}</Btn>
            <Btn onClick={() => { setShowForm(false); setEditId(null); }} variant="ghost">Cancel</Btn>
          </div>
        </Modal>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 flex justify-between items-center gap-3">
          <h3 className="font-semibold text-gray-700 text-sm">Maintenance History</h3>
          <div className="flex items-center gap-3">
            {openCount > 0 && <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="instrument-maint-open">{openCount} open</span>}
            <span className="text-xs text-gray-400">{records.length} {records.length === 1 ? "record" : "records"}</span>
            {can(user, "addHistory") && <Btn onClick={startAdd} testId="add-instrument-maint-btn">+ Add Maintenance Record</Btn>}
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading maintenance history...</div>
        ) : records.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400" data-testid="instrument-maint-empty">
            No maintenance recorded for this instrument yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b">{["#", "Date", "Event", "Type", "Work", "Rectification", "Spare Part", "Supervisor", "Status", "Files", ""].map(h => <th key={h} className="text-left px-3 py-2 text-xs text-gray-500 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {records.map((r, n) => {
                  const badge = MT_BADGE[maintenanceTypeOf(r)] || MT_BADGE.breakdown;
                  const closed = !!String(r.endDate || "").trim();
                  const ext = r.workType === "external";
                  return (
                    <tr key={r.id} className="border-b hover:bg-blue-50 transition-colors" data-testid={`instrument-maint-row-${r.id}`}>
                      <td className="px-3 py-2 text-gray-400">{n + 1}</td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{r.startDate}{r.startTime ? ` ${formatTime12(r.startTime, r.createdAt)}` : ""}</td>
                      <td className="px-3 py-2 font-medium text-gray-800">{r.description || "—"}</td>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-xs font-bold ${badge.cls}`} title={MT_LABELS[maintenanceTypeOf(r)]}>{badge.text}</span></td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded font-semibold ${ext ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>{ext ? "External" : "Internal"}</span>
                        {ext && r.vendorName ? <div className="text-gray-500 mt-0.5">{r.vendorName}</div> : null}
                        {(r.poNumber || r.poAmount) ? (
                          <div className="text-gray-400 mt-0.5">
                            {r.paymentMode === "cash" ? "Cash" : r.poNumber || "PO"}
                            {r.poAmount ? ` · ₹${Number(r.poAmount).toLocaleString("en-IN")}` : ""}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{r.rectification || "—"}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{r.sparePart || "—"}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{r.supervisor || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${closed ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                          title={closed ? `Closed on ${r.endDate}${r.endTime ? ` ${formatTime12(r.endTime, r.createdAt)}` : ""}` : "Still open"}>
                          {closed ? "Closed" : "Open"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs space-y-0.5">
                        {[["Before", r.beforeReportFilePath, r.beforeReportFileName],
                          ["After", r.reportFilePath, r.reportFileName],
                          [r.paymentMode === "cash" ? "Bill" : "PO", r.poFilePath, r.poFileName]]
                          .filter(([, path]) => !!path)
                          .map(([label, path, fname]) => (
                            <button key={label} onClick={(ev) => openProtectedFile(ev, path, fname)} className="block text-blue-500 hover:text-blue-700 underline" data-testid={`instrument-maint-file-${label}-${r.id}`}>{label}</button>
                          ))}
                        {!r.beforeReportFilePath && !r.reportFilePath && !r.poFilePath && <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 whitespace-nowrap">
                          {can(user, "editHistory") && <button onClick={() => startEdit(r)} className="text-gray-400 hover:text-gray-600 text-xs" data-testid={`instrument-maint-edit-${r.id}`}>Edit</button>}
                          {can(user, "deleteHistory") && <button onClick={() => deleteRecord(r)} className="text-red-300 hover:text-red-500 text-xs ml-1" data-testid={`instrument-maint-delete-${r.id}`}>Del</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
